// XNGAGE CONFIDENTIAL
// __________________________
//
// Copyright (C) 2022 Xngage - All Rights Reserved
//
// All code or information contained herein is, and remains the
// property of Xngage LLC and its customers.  The intellectual
// and technical concepts contained are proprietary to Xngage LLC
// and may be covered by U.S. and Foreign Patents, patents in
// process, and are protected by trade secret or copyright law.
// Dissemination of this information or reproduction of this material
// is strictly forbidden unless prior written permission is obtained
// from Xngage LLC.
//

import { contentModeCookieName } from "@insite/client-framework/Common/ContentMode";
import { getCookie } from "@insite/client-framework/Common/Cookies";
import StyledWrapper from "@insite/client-framework/Common/StyledWrapper";
import openPrintDialog from "@insite/client-framework/Common/Utilities/openPrintDialog";
import { HasProduct, withProduct } from "@insite/client-framework/Components/ProductContext";
import Zone from "@insite/client-framework/Components/Zone";
import translate from "@insite/client-framework/Translate";
import WidgetModule from "@insite/client-framework/Types/WidgetModule";
import WidgetProps from "@insite/client-framework/Types/WidgetProps";
import { BaseTheme } from "@insite/mobius/globals/baseTheme";
import Hidden from "@insite/mobius/Hidden";
import Link from "@insite/mobius/Link";
import Typography, { TypographyPresentationProps } from "@insite/mobius/Typography";
import { breakpointMediaQueries } from "@insite/mobius/utilities";
import getColor from "@insite/mobius/utilities/getColor";
import InjectableCss from "@insite/mobius/utilities/InjectableCss";
import VisuallyHidden from "@insite/mobius/VisuallyHidden";
import setBreadcrumbActions from "@mcmc/App/Handlers/Layout/setBreadcrumbActions";
import { totalSectionGlobalStyles } from "@mcmc/App/WidgetExtensions/AccountPagesGlobalStyle";
import ProductShareLink, { ProductShareLinkStyles } from "@mcmc/Overrides/Components/ProductShareLink";
import Share from "@mcmc/XDL/Icons/Share";
import { mergeWithCss } from "@mcmc/XDL/Utilities";
import { layoutContainer, printIconStyle } from "@mcmc/XDL/Utilities/sharedStyles";
import * as React from "react";
import { useEffect } from "react";
import { connect, ResolveThunks } from "react-redux";
import { css } from "styled-components";

const mapDispatchToProps = {
    setBreadcrumbActions,
};

const printLinkClickHandler = () => {
    openPrintDialog();
};

type Props = WidgetProps & ResolveThunks<typeof mapDispatchToProps> & HasProduct;

export interface ProductDetailsViewStyles {
    layoutContainer?: InjectableCss;
    breadcrumbActionsWrapper?: InjectableCss;
    shareLink?: ProductShareLinkStyles;
    wrapper?: InjectableCss;
    zoneTitle?: TypographyPresentationProps;
    row?: InjectableCss;
    mainContentColumn?: InjectableCss;
    titleColumn?: InjectableCss;
    imageColumn?: InjectableCss;
    detailsColumn?: InjectableCss;
    partNumbersColumn?: InjectableCss;
    partNumbersBlock?: InjectableCss;
    sectionTitle?: TypographyPresentationProps;
    tabsWrapper?: InjectableCss;
}

const columnPadding = 15;
const imageWidth = 335;
const partNumbersWidth = 330;
const imageColumnWidth = imageWidth + columnPadding * 2;
const partNumbersColumnWidth = partNumbersWidth + columnPadding * 2;

const columnStyle = css`
    float: left;
    padding: 0 ${columnPadding}px;

    ${({ theme }: { theme: BaseTheme }) =>
        breakpointMediaQueries(
            theme,
            [
                null,
                css`
                    float: none;
                    padding-left: 0;
                    padding-right: 0;
                `,
            ],
            "max",
        )}
`;

export const pdpColumnStyle = columnStyle;

export const viewStyles: ProductDetailsViewStyles = {
    layoutContainer: {
        css: layoutContainer,
    },
    breadcrumbActionsWrapper: mergeWithCss(totalSectionGlobalStyles.breadcrumbActionsWrapper, {
        css: css`
            flex: 1 1 auto;
            justify-content: flex-end;
        `,
    }),
    shareLink: {
        link: {
            icon: {
                iconProps: {
                    src: Share,
                    color: "secondary",
                    css: css`
                        margin: 0;
                    `,
                },
            },
            typographyProps: {
                size: 16,
                css: css`
                    margin: 0;
                `,
            },
        },
    },
    zoneTitle: {
        weight: "normal",
        size: 16,
        color: "primary.contrast",
        css: css`
            padding: 2px 5px;
            background-color: ${getColor("text.main")};
            display: block;
            margin: 10px 5px -8px 5px;
        `,
    },
    row: {
        css: css`
            display: block;
            margin: 0 -${columnPadding}px;
            &:before,
            &:after {
                content: "";
                display: block;
                width: 100%;
                clear: both;
            }

            ${({ theme }: { theme: BaseTheme }) =>
                breakpointMediaQueries(
                    theme,
                    [
                        null,
                        css`
                            margin-left: 0;
                            margin-right: 0;
                        `,
                    ],
                    "max",
                )}
        `,
    },
    mainContentColumn: {
        css: [
            columnStyle,
            css`
                width: calc(100% - ${partNumbersColumnWidth}px);
                margin-bottom: 25px;

                ${({ theme }: { theme: BaseTheme }) =>
                    breakpointMediaQueries(
                        theme,
                        [
                            null,
                            null,
                            css`
                                width: 100%;
                                float: none;
                            `,
                        ],
                        "max",
                    )}
            `,
        ],
    },
    titleColumn: {
        css: [
            columnStyle,
            css`
                float: right;
                width: calc(100% - ${imageColumnWidth}px);

                ${({ theme }: { theme: BaseTheme }) =>
                    breakpointMediaQueries(
                        theme,
                        [
                            null,
                            null,
                            null,
                            css`
                                width: 100%;
                                margin-bottom: 20px;
                            `,
                        ],
                        "max",
                    )}
            `,
        ],
    },
    imageColumn: {
        css: [
            columnStyle,
            css`
                width: ${imageColumnWidth}px;

                ${({ theme }: { theme: BaseTheme }) =>
                    breakpointMediaQueries(
                        theme,
                        [
                            null,
                            css`
                                && {
                                    width: 100%;
                                }
                            `,
                            null,
                            css`
                                width: 50%;
                            `,
                        ],
                        "max",
                    )}
            `,
        ],
    },
    detailsColumn: {
        css: [
            columnStyle,
            css`
                width: calc(100% - ${imageColumnWidth}px);
                float: right;

                ${({ theme }: { theme: BaseTheme }) =>
                    breakpointMediaQueries(
                        theme,
                        [
                            null,
                            css`
                                && {
                                    width: 100%;
                                }
                            `,
                            null,
                            css`
                                width: 50%;
                            `,
                        ],
                        "max",
                    )}
            `,
        ],
    },
    partNumbersColumn: {
        css: [
            columnStyle,
            css`
                width: ${partNumbersColumnWidth}px;
                max-width: 100%;
                margin: 0 auto 25px auto;

                ${({ theme }: { theme: BaseTheme }) =>
                    breakpointMediaQueries(
                        theme,
                        [
                            null,
                            null,
                            css`
                                float: none;
                            `,
                        ],
                        "max",
                    )}
            `,
        ],
    },
    partNumbersBlock: {
        css: css`
            margin-bottom: 20px;

            .mcmc-part-number {
                &:nth-child(odd) {
                    background-color: ${getColor("common.ui3")};
                }
            }
        `,
    },
    sectionTitle: {
        variant: "h6",
        forwardAs: "h2",
        lineHeight: "19px",
        css: css`
            margin-bottom: 10px;
        `,
    },
};

const styles = viewStyles;

const ProductDetailsView: React.FC<Props> = ({ id, product, productInfo, setBreadcrumbActions }) => {
    const isCmsEditing = getCookie(contentModeCookieName) === "Editing";
    useEffect(() => {
        setBreadcrumbActions({
            breadcrumbActions: (
                <StyledWrapper {...styles.breadcrumbActionsWrapper}>
                    <ProductShareLink
                        productProp={product}
                        productInfoProp={productInfo}
                        extendedStyles={styles.shareLink}
                        text=" "
                    />

                    <Link
                        {...printIconStyle}
                        onClick={printLinkClickHandler}
                        aria-label={translate(`Print Product Details`)}
                    >
                        <VisuallyHidden>{translate("Print")}</VisuallyHidden>
                    </Link>
                </StyledWrapper>
            ),
        });

        return () => {
            setBreadcrumbActions({
                breadcrumbActions: null,
            });
        };
    }, []);

    return (
        <>
            <StyledWrapper {...styles.layoutContainer}>
                <StyledWrapper {...styles.row}>
                    <StyledWrapper {...styles.mainContentColumn}>
                        <StyledWrapper {...styles.row}>
                            <StyledWrapper {...styles.titleColumn}>
                                {isCmsEditing && (
                                    <Typography {...styles.zoneTitle}>{translate("Title Zone")}</Typography>
                                )}
                                <Zone contentId={id} zoneName="Title Zone" />
                            </StyledWrapper>
                            <StyledWrapper {...styles.imageColumn}>
                                {isCmsEditing && (
                                    <Typography {...styles.zoneTitle}>{translate("Image Zone")}</Typography>
                                )}
                                <Zone contentId={id} zoneName="Image Zone" />
                            </StyledWrapper>
                            <StyledWrapper {...styles.detailsColumn}>
                                {isCmsEditing && (
                                    <Typography {...styles.zoneTitle}>{translate("Details Zone")}</Typography>
                                )}
                                <Zone contentId={id} zoneName="Details Zone" />
                                <Hidden above="md">
                                    <StyledWrapper {...styles.partNumbersBlock}>
                                        <Typography {...styles.sectionTitle}>{translate("Details")}</Typography>
                                        <Zone contentId={id} zoneName="Part Numbers Zone" />
                                    </StyledWrapper>
                                </Hidden>
                                <Hidden above="md">
                                    <StyledWrapper {...styles.partNumbersBlock}>
                                        <Zone contentId={id} zoneName="Availability Zone" />
                                    </StyledWrapper>
                                </Hidden>
                                {isCmsEditing && (
                                    <Typography {...styles.zoneTitle}>{translate("Actions Zone")}</Typography>
                                )}
                                <Zone contentId={id} zoneName="Actions Zone" />
                            </StyledWrapper>
                        </StyledWrapper>
                    </StyledWrapper>
                    <StyledWrapper {...styles.partNumbersColumn}>
                        <Hidden below="lg">
                            <StyledWrapper {...styles.partNumbersBlock}>
                                <Typography {...styles.sectionTitle}>{translate("Details")}</Typography>
                                {isCmsEditing && (
                                    <Typography {...styles.zoneTitle}>{translate("Part Numbers Zone")}</Typography>
                                )}
                                <Zone contentId={id} zoneName="Part Numbers Zone" />
                            </StyledWrapper>
                            <StyledWrapper {...styles.partNumbersBlock}>
                                {isCmsEditing && (
                                    <Typography {...styles.zoneTitle}>{translate("Availability Zone")}</Typography>
                                )}
                                <Zone contentId={id} zoneName="Availability Zone" />
                            </StyledWrapper>
                        </Hidden>
                        <Zone contentId={id} zoneName="Extra Zone" />
                    </StyledWrapper>
                </StyledWrapper>
            </StyledWrapper>
            <StyledWrapper {...styles.tabsWrapper}>
                {isCmsEditing && <Typography {...styles.zoneTitle}>{translate("Tabs Zone")}</Typography>}
                <Zone contentId={id} zoneName="Content1" />
            </StyledWrapper>
        </>
    );
};

const widgetModule: WidgetModule = {
    component: withProduct(connect(undefined, mapDispatchToProps)(ProductDetailsView)),
    definition: {
        displayName: "View",
        group: "Product Details",
        allowedContexts: ["ProductDetailsPage"],
    },
};

export default widgetModule;
